import React, { CSSProperties, useRef, useState } from 'react';
import { CODE, RESPONSE } from '../content';

// The API example as two tiles: the request, in a language of your choice, and the answer it
// gets back. Each shows its code shortened so its shape reads at a glance; Copy takes it in
// full. The languages share one cell, so switching never changes the tile's height.

interface TileProps {
  className?: string;
  style?: CSSProperties;
}

// Each line is its own block, so an empty one keeps its height. Comments read quieter than
// the code around them.
function Lines({ code, comment }: { code: string; comment?: string }) {
  return (
    <code>
      {code.split('\n').map((line, i) => {
        const at = comment ? line.indexOf(comment) : -1;
        return (
          <span key={i} className="oh-code-line">
            {at < 0 ? (
              line || ' '
            ) : (
              <>
                {line.slice(0, at)}
                <span className="oh-code-comment">{line.slice(at)}</span>
              </>
            )}
          </span>
        );
      })}
    </code>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState('done');
    } catch {
      setState('failed');
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 1600);
  };
  return (
    <button type="button" onClick={copy} className="oh-code-copy" aria-label={label}>
      {/* Once copied, the icon is a tick in the same box. */}
      <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
        {state === 'done' ? (
          <path d="M2.75 8.5l3.5 3.5 7-7.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <>
            <rect x="5" y="5" width="9" height="9" rx="2" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <path d="M11 3.5V3a1.5 1.5 0 0 0-1.5-1.5H3A1.5 1.5 0 0 0 1.5 3v6.5A1.5 1.5 0 0 0 3 11h.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
          </>
        )}
      </svg>
      {/* Copy and Copied share one place, as wide as Copied, so the icon stays put. */}
      <span className="oh-code-copy-text" aria-live="polite">
        {state === 'failed' ? (
          'Select to copy'
        ) : (
          <>
            <span className={state === 'done' ? 'is-off' : undefined}>Copy</span>
            <span className={state === 'done' ? undefined : 'is-off'}>Copied</span>
          </>
        )}
      </span>
    </button>
  );
}

export function RequestTile({ className = '', style }: TileProps) {
  const [lang, setLang] = useState(0);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const select = (i: number) => {
    const next = (i + CODE.length) % CODE.length;
    setLang(next);
    tabs.current[next]?.focus();
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') select(lang + 1);
    if (e.key === 'ArrowLeft') select(lang - 1);
  };

  return (
    <div className={`oh-tile oh-code ${className}`} style={style}>
      <div className="oh-code-head">
        <div role="tablist" aria-label="Request language" className="oh-code-tabs" onKeyDown={onKey}>
          {CODE.map((c, i) => (
            <button
              key={c.label}
              ref={(el) => { tabs.current[i] = el; }}
              type="button"
              role="tab"
              id={`oh-code-tab-${i}`}
              aria-selected={i === lang}
              aria-controls={`oh-code-panel-${i}`}
              tabIndex={i === lang ? 0 : -1}
              onClick={() => setLang(i)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <CopyButton text={CODE[lang].full} label={`Copy the full ${CODE[lang].label} request`} />
      </div>
      <div className="oh-code-stack">
        {CODE.map((c, i) => (
          <pre
            key={c.label}
            id={`oh-code-panel-${i}`}
            role="tabpanel"
            aria-labelledby={`oh-code-tab-${i}`}
            tabIndex={i === lang ? 0 : -1}
            className={i === lang ? undefined : 'is-off'}
          >
            <Lines code={c.shown} comment={c.comment} />
          </pre>
        ))}
      </div>
    </div>
  );
}

export function ResponseTile({ className = '', style }: TileProps) {
  return (
    <div className={`oh-tile oh-code ${className}`} style={style}>
      <div className="oh-code-head">
        <span className="oh-code-label">
          Response
          <span className="oh-code-status">200 OK</span>
        </span>
        <CopyButton text={RESPONSE.full} label="Copy the full response" />
      </div>
      <pre tabIndex={0}>
        <Lines code={RESPONSE.shown} />
      </pre>
    </div>
  );
}
